import { RingBuffer } from './ring-buffer';

const TARGET_SAMPLE_RATE = 16000;
const BUFFER_DURATION_S = 300;
const ANALYSER_FFT_SIZE = 2048;

export interface AudioCaptureOptions {
  bufferDurationS?: number;
  /** Fires once when the recording hits the max duration and auto-stops. */
  onLimitReached?: () => void;
}

/**
 * Captures microphone audio, resamples to 16kHz mono for Whisper,
 * and exposes an AnalyserNode for realtime visualization.
 */
export class AudioCapture {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private _ringBuffer: RingBuffer | null = null;
  private _recording = false;
  private _limitReached = false;
  private recordedSamples = 0;
  private capacitySamples = 0;
  private readonly bufferDurationS: number;
  private readonly onLimitReached?: () => void;

  constructor(opts: AudioCaptureOptions = {}) {
    this.bufferDurationS = opts.bufferDurationS ?? BUFFER_DURATION_S;
    this.onLimitReached = opts.onLimitReached;
  }

  get maxDurationS(): number {
    return this.bufferDurationS;
  }

  get recordedDurationS(): number {
    return this.recordedSamples / TARGET_SAMPLE_RATE;
  }

  get ringBuffer(): RingBuffer | null {
    return this._ringBuffer;
  }

  get recording(): boolean {
    return this._recording;
  }

  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  async requestMic(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    this.audioContext = new AudioContext();
    const nativeSampleRate = this.audioContext.sampleRate;

    this.capacitySamples = TARGET_SAMPLE_RATE * this.bufferDurationS;
    this._ringBuffer = new RingBuffer(this.capacitySamples, TARGET_SAMPLE_RATE);

    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = ANALYSER_FFT_SIZE;
    this.analyserNode.smoothingTimeConstant = 0.8;
    this.sourceNode.connect(this.analyserNode);

    const bufferSize = 4096;
    this.processorNode = this.audioContext.createScriptProcessor(
      bufferSize,
      1,
      1
    );

    this.processorNode.onaudioprocess = (event) => {
      if (!this._recording) return;
      const remaining = this.capacitySamples - this.recordedSamples;
      if (remaining <= 0) return;
      const input = event.inputBuffer.getChannelData(0);
      const resampled = resample(input, nativeSampleRate, TARGET_SAMPLE_RATE);
      const toWrite =
        resampled.length <= remaining
          ? resampled
          : resampled.subarray(0, remaining);
      this._ringBuffer!.write(toWrite);
      this.recordedSamples += toWrite.length;
      if (this.recordedSamples >= this.capacitySamples && !this._limitReached) {
        this._limitReached = true;
        this._recording = false;
        this.onLimitReached?.();
      }
    };

    this.sourceNode.connect(this.processorNode);
    this.processorNode.connect(this.audioContext.destination);
  }

  startRecording(): void {
    if (!this._ringBuffer) {
      throw new Error('Mic not initialized. Call requestMic() first.');
    }
    this._ringBuffer.clear();
    this.recordedSamples = 0;
    this._limitReached = false;
    this._recording = true;
  }

  stopRecording(): void {
    this._recording = false;
  }

  getRecordedAudio(): Float32Array {
    if (!this._ringBuffer) return new Float32Array(0);
    return this._ringBuffer.readAll();
  }

  dispose(): void {
    this._recording = false;

    this.processorNode?.disconnect();
    this.analyserNode?.disconnect();
    this.sourceNode?.disconnect();
    this.processorNode = null;
    this.analyserNode = null;
    this.sourceNode = null;

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
    this.audioContext = null;

    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }

    this._ringBuffer = null;
  }
}

function resample(
  input: Float32Array,
  sourceSR: number,
  targetSR: number
): Float32Array {
  if (sourceSR === targetSR) return input;

  const ratio = sourceSR / targetSR;
  const outputLength = Math.ceil(input.length / ratio);
  const output = new Float32Array(outputLength);

  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const low = Math.floor(srcIndex);
    const high = Math.min(low + 1, input.length - 1);
    const frac = srcIndex - low;
    output[i] = input[low] * (1 - frac) + input[high] * frac;
  }

  return output;
}
